import React, { useState, Fragment } from 'react'
import { Helmet } from 'react-helmet'
import NavbarInteractive from '../components/navbar-interactive'
import Footer from '../components/footer'
import './why-apollo.css'

const WhyApollo = (props) => {
  const [activePersona, setActivePersona] = useState('cpo') // 'cpo' or 'cio'

  const handleDemoLink = () => {
    window.open('https://outlook.office.com/book/RequestDemoSchedulingPage@scmmax.com/?ismsaljsauthenabled', '_blank');
  }

  return (
    <div className="why-apollo-container">
      <Helmet>
        <title>Why Apollo — SCMmax Apollo</title>
        <meta
          name="description"
          content="Discover why Apollo is the domain layer that transforms data access into genuine procurement intelligence."
        />
      </Helmet>
      
      <NavbarInteractive
        primaryButtonTest={
          <Fragment>
            <span className="home-text22">Request Demo</span>
          </Fragment>
        }
      ></NavbarInteractive>

      {/* ── HERO ── */}
      <section className="why-apollo-hero">
        <div className="why-apollo-hero-inner">
          <div className="why-apollo-audience-tags">
            <span className="why-apollo-aud-tag cxo">◆ For CPOs & CFOs</span>
            <span className="why-apollo-aud-tag cio">◆ For CIOs & IT Leaders</span>
          </div>
          <h1 className="why-apollo-hero-hl">
            Your AI has access to your data.<br />
            It does not understand<br />
            <em>what your data means.</em>
          </h1>
          <p className="why-apollo-hero-sub">
            Connecting ChatGPT, Claude, or Gemini to your ERP and data sources is not the same as giving your organisation an intelligent procurement system. Apollo is the domain layer that transforms data access into genuine procurement intelligence — understanding context, learning your organisation's judgment, and compounding in value over time.
          </p>
          <div className="why-apollo-hero-cta">
            <button className="btn-primary" onClick={handleDemoLink}>Request Executive Briefing</button>
            <button className="btn-ghost" onClick={() => window.location.hash = 'ProductList1'}>See the Product</button>
          </div>

          <div className="why-apollo-hero-strip">
            <div className="why-apollo-hero-strip-item">
              <div className="why-apollo-hsi-num">3<em>×</em></div>
              <div className="why-apollo-hsi-label"><strong>Dimensions of context</strong> Apollo attaches to every data point — provenance, epistemic status, and organisational definition</div>
            </div>
            <div className="why-apollo-hero-strip-item">
              <div className="why-apollo-hsi-num">74<em>%</em></div>
              <div className="why-apollo-hsi-label"><strong>Reduction in AI token cost</strong> per query through domain-filtered retrieval — only relevant, correctly-contextualised data reaches the model</div>
            </div>
            <div className="why-apollo-hero-strip-item">
              <div className="why-apollo-hsi-num"><em>↑</em></div>
              <div className="why-apollo-hsi-label"><strong>Decision autonomy increases</strong> over time as Apollo accumulates your organisation's decision traces and learns its judgment patterns</div>
            </div>
          </div>
        </div>
      </section>

      <div className="why-apollo-wave" style={{ background: 'var(--white)' }}>
        <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 0 L1440 0 L1440 24 Q720 64 0 24 Z" fill="#0F1421" />
        </svg>
      </div>

      {/* ── PILLAR 1: DATA CONTEXT ── */}
      <section className="why-apollo-sec-light">
        <div className="why-apollo-sec-inner">
          <div className="why-apollo-sec-header">
            <div className="why-apollo-s-eyebrow why-apollo-brass-ey">
              <span className="why-apollo-s-eyebrow-line why-apollo-brass-ln"></span>The Context Problem
            </div>
            <h2 className="why-apollo-s-title">Your AI gets the number.<br /><em>Apollo gets what the number means.</em></h2>
            <p className="why-apollo-s-sub">Data without context is noise. Every figure in your procurement landscape — a price, a market signal, a spend total — carries metadata that determines whether it is relevant, reliable, and correctly interpreted for your specific decision. Generic AI discards that metadata. Apollo is built around it.</p>
          </div>

          <div className="why-apollo-context-grid">
            <div className="why-apollo-context-left">
              <div className="why-apollo-context-points">
                <div className="why-apollo-ctx-point">
                  <div className="why-apollo-ctx-icon brass">💲</div>
                  <div>
                    <div className="why-apollo-ctx-title">Price data carries provenance — not just a number</div>
                    <div className="why-apollo-ctx-body">A commodity price means nothing until you know: the delivery location and Incoterms, the commodity grade and specification, the currency and the FX valuation date, and critically — the price type. <strong>Spot, futures, contract, index, historical average, or assessed price</strong> are not interchangeable. A Platts assessed price and a broker spot quote should never be weighted the same in a buying decision. Apollo passes the price and its full provenance as typed, structured fields. A generic AI sees "$9,240" and reasons blindly.</div>
                  </div>
                </div>

                <div className="why-apollo-ctx-point">
                  <div className="why-apollo-ctx-icon teal">📰</div>
                  <div>
                    <div className="why-apollo-ctx-title">Market signals require epistemic classification</div>
                    <div className="why-apollo-ctx-body">A supply disruption report, an analyst forecast, and a confirmed production outage are all "news about your commodity." But they carry entirely different weights in a sourcing decision. Apollo classifies every external signal by its epistemic status: <strong>verified fact, trend signal, analyst opinion, forecast with confidence interval, or commodity-adjacent signal</strong> — information not about the commodity itself but about an upstream input, a substitute, or a demand driver. Generic AI treats all of them as equivalent text.</div>
                  </div>
                </div>

                <div className="why-apollo-ctx-point">
                  <div className="why-apollo-ctx-icon slate">🏢</div>
                  <div>
                    <div className="why-apollo-ctx-title">Internal data has definitional ambiguity — by design</div>
                    <div className="why-apollo-ctx-body">Ask your ERP "what was Q3 revenue?" and you may get three different answers — all technically correct. Revenue recognised, invoiced, or cash-received. Spend committed, approved, or actuals. <strong>The same metric defined differently by Finance, Procurement, and Operations is not a data quality problem — it is a definitional context problem.</strong> Apollo knows the user's role, their department, and the decision they are trying to make. It applies the correct semantic definition before retrieval, ensuring the number returned is the one actually intended.</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="why-apollo-ctx-example">
                <div className="why-apollo-ctx-ex-header">Apollo context layer — live example</div>
                <div className="why-apollo-ctx-ex-body">
                  <div className="why-apollo-ctx-ex-title">Same commodity — three prices — three different meanings</div>
                  <div className="why-apollo-price-row">
                    <div className="why-apollo-price-item">
                      <div className="why-apollo-price-value"><em>$</em>9,240 <span style={{ fontSize: '.75rem', color: 'var(--slate-400)', fontWeight: 3 }}>/MT</span></div>
                      <div className="why-apollo-price-tags">
                        <span className="why-apollo-p-tag teal">Spot — LME</span>
                        <span className="why-apollo-p-tag brass">Grade A Copper</span>
                        <span className="why-apollo-p-tag slate">USD · Today</span>
                        <span className="why-apollo-p-tag slate">CIF Rotterdam</span>
                        <span className="why-apollo-p-tag teal">Actionable for spot buy</span>
                      </div>
                    </div>
                    <div className="why-apollo-price-item">
                      <div className="why-apollo-price-value"><em>$</em>9,580 <span style={{ fontSize: '.75rem', color: 'var(--slate-400)', fontWeight: 3 }}>/MT</span></div>
                      <div className="why-apollo-price-tags">
                        <span className="why-apollo-p-tag brass">3-month Futures</span>
                        <span className="why-apollo-p-tag slate">Grade A Copper</span>
                        <span className="why-apollo-p-tag slate">USD · Today</span>
                        <span className="why-apollo-p-tag red">Not for spot comparison</span>
                      </div>
                    </div>
                    <div className="why-apollo-price-item">
                      <div className="why-apollo-price-value"><em>$</em>8,910 <span style={{ fontSize: '.75rem', color: 'var(--slate-400)', fontWeight: 3 }}>/MT</span></div>
                      <div className="why-apollo-price-tags">
                        <span className="why-apollo-p-tag slate">Contract average · Q2</span>
                        <span className="why-apollo-p-tag slate">Mixed grades</span>
                        <span className="why-apollo-p-tag red">Historical — not current</span>
                      </div>
                    </div>
                  </div>
                  <div className="why-apollo-ctx-verdict">
                    Apollo selects <strong>$9,240 LME spot</strong> as the benchmark for this negotiation — not because it is the lowest, but because it carries the correct provenance context for a spot buying decision.
                  </div>

                  <div style={{ height: '20px' }}></div>
                  <div className="why-apollo-ctx-ex-title">Market signals — classified before reasoning</div>
                  <div className="why-apollo-news-items">
                    <div className="why-apollo-news-item">
                      <div className="why-apollo-news-headline">"Chilean mine production down 12% in Q3 due to labour dispute"</div>
                      <div className="why-apollo-news-tags">
                        <span className="why-apollo-p-tag teal">Verified fact</span>
                        <span className="why-apollo-p-tag brass">Supply-side · Direct</span>
                        <span className="why-apollo-p-tag teal">High weight</span>
                      </div>
                    </div>
                    <div className="why-apollo-news-item">
                      <div className="why-apollo-news-headline">"Analysts forecast copper demand surge driven by EV battery uptake"</div>
                      <div className="why-apollo-news-tags">
                        <span className="why-apollo-p-tag slate">Analyst opinion</span>
                        <span className="why-apollo-p-tag slate">Demand-side · Adjacent</span>
                        <span className="why-apollo-p-tag brass">Medium weight</span>
                      </div>
                    </div>
                    <div className="why-apollo-news-item">
                      <div className="why-apollo-news-headline">"Fed rate decision could strengthen USD in H2"</div>
                      <div className="why-apollo-news-tags">
                        <span className="why-apollo-p-tag slate">Macro signal · Indirect</span>
                        <span className="why-apollo-p-tag red">FX impact only</span>
                        <span className="why-apollo-p-tag red">Low weight for pricing</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="why-apollo-wave" style={{ background: 'var(--slate-50)' }}>
        <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 0 L1440 0 L1440 24 Q720 64 0 24 Z" fill="#FFFFFF" />
        </svg>
      </div>

      {/* ── PILLAR 2: DECISION INTELLIGENCE ── */}
      <section className="why-apollo-sec-surface">
        <div className="why-apollo-sec-inner">
          <div className="why-apollo-sec-header">
            <div className="why-apollo-s-eyebrow why-apollo-brass-ey">
              <span className="why-apollo-s-eyebrow-line why-apollo-brass-ln"></span>Organisational Decision Intelligence
            </div>
            <h2 className="why-apollo-s-title">Generic AI answers questions.<br /><em>Apollo learns how your organisation decides.</em></h2>
            <p className="why-apollo-s-sub">The most valuable thing about an experienced procurement leader is not what they know — it is how they decide. The patterns they have internalised. The criteria they apply consistently even when they are unstated. Apollo is the only AI layer built to capture, model, and compound that organisational judgment.</p>
          </div>

          <div className="why-apollo-decision-grid">
            <div className="why-apollo-decision-left">
              <div className="why-apollo-s-eyebrow why-apollo-slate-ey" style={{ marginBottom: '20px' }}>
                <span className="why-apollo-s-eyebrow-line why-apollo-slate-ln"></span>The maturity journey
              </div>
              <div className="why-apollo-maturity-track">
                <div className="why-apollo-maturity-stage">
                  <div className="why-apollo-ms-marker">
                    <div className="why-apollo-ms-circle done">✓</div>
                    <div className="why-apollo-ms-line"></div>
                  </div>
                  <div className="why-apollo-ms-content" style={{ paddingBottom: '16px' }}>
                    <div className="why-apollo-ms-label done">From day one</div>
                    <h4 className="why-apollo-ms-title">Apollo reads your decision history</h4>
                    <p className="why-apollo-ms-body">Meeting transcripts. Email approval chains. Contract award decisions. Rejection rationales. Apollo ingests these not as documents but as structured decision traces — mapping who decided what, under what conditions, and with what outcome.</p>
                  </div>
                </div>

                <div className="why-apollo-maturity-stage">
                  <div className="why-apollo-ms-marker">
                    <div className="why-apollo-ms-circle done">✓</div>
                    <div className="why-apollo-ms-line"></div>
                  </div>
                  <div className="why-apollo-ms-content" style={{ paddingBottom: '16px' }}>
                    <div className="why-apollo-ms-label done">Within weeks</div>
                    <h4 className="why-apollo-ms-title">Unstated criteria become visible</h4>
                    <p className="why-apollo-ms-body">Apollo surfaces the patterns that are never written in policy — the CFO who consistently rejects suppliers with debt-to-equity above a threshold, the category manager who always favours dual-source even when cost says single. <strong>Cultural intelligence that no generic AI can infer from an ERP query.</strong></p>
                  </div>
                </div>

                <div className="why-apollo-maturity-stage">
                  <div className="why-apollo-ms-marker">
                    <div className="why-apollo-ms-circle now">→</div>
                    <div className="why-apollo-ms-line"></div>
                  </div>
                  <div className="why-apollo-ms-content" style={{ paddingBottom: '16px' }}>
                    <div className="why-apollo-ms-label now">Ongoing</div>
                    <h4 className="why-apollo-ms-title">Recommendations carry organisational confidence</h4>
                    <p className="why-apollo-ms-body">Apollo does not just recommend — it tells you how confident it is based on how many analogous decisions your organisation has made before, and what the outcomes were. High-confidence decisions can move with minimal validation. Novel decisions are flagged for human review.</p>
                  </div>
                </div>

                <div className="why-apollo-maturity-stage">
                  <div className="why-apollo-ms-marker">
                    <div className="why-apollo-ms-circle future">◇</div>
                  </div>
                  <div className="why-apollo-ms-content">
                    <div className="why-apollo-ms-label future">Over time</div>
                    <h4 className="why-apollo-ms-title">Human-in-the-loop requirement reduces</h4>
                    <p className="why-apollo-ms-body">As Apollo accumulates decision traces and validates outcomes, routine decisions — standard supplier renewals, category reorders within policy, low-risk contract extensions — execute autonomously. Your team's attention is preserved for decisions that genuinely need it.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="why-apollo-decision-right">
              <div className="why-apollo-s-eyebrow why-apollo-slate-ey" style={{ marginBottom: '20px' }}>
                <span className="why-apollo-s-eyebrow-line why-apollo-slate-ln"></span>Apollo decision engine — live
              </div>
              <div className="why-apollo-decision-card">
                <div className="why-apollo-dc-header">
                  <div className="why-apollo-t-dot" style={{ background: 'rgba(45,191,126,.6)' }}></div>
                  <div className="why-apollo-dc-title">Pending procurement decisions — confidence scored</div>
                </div>
                <div className="why-apollo-dc-item">
                  <div className="why-apollo-dc-item-top">
                    <div className="why-apollo-dc-item-q">"Renew Grainger MRO contract for 12 months at current terms?"</div>
                    <div className="why-apollo-dc-confidence why-apollo-conf-high">92% confidence</div>
                  </div>
                  <div className="why-apollo-dc-context">Apollo has observed 14 analogous renewals in this category. Your organisation accepted renewal at current terms in 13 of 14 cases where supplier performance scored above 88. Grainger scores 91. CFO approval was required in 2 of 14 — both where spend exceeded £2M. This contract is £840K.</div>
                  <div className="why-apollo-dc-trace"><span className="why-apollo-trace-dot"></span>Traced from 14 prior decisions · 2 email threads · 1 board approval chain</div>
                </div>
                <div className="why-apollo-dc-item">
                  <div className="why-apollo-dc-item-top">
                    <div className="why-apollo-dc-item-q">"Award RFQ for specialty chemicals to Supplier B at 4% below incumbent?"</div>
                    <div className="why-apollo-dc-confidence why-apollo-conf-med">61% confidence</div>
                  </div>
                  <div className="why-apollo-dc-context">Your organisation has historically favoured dual-source in specialty chemicals even when cost favours single-source (observed in 7 of 9 prior decisions). This RFQ proposes single-source. Flagging for CPO review — cost saving is real but conflicts with observed risk posture.</div>
                  <div className="why-apollo-dc-trace"><span className="why-apollo-trace-dot"></span>Traced from 9 prior decisions · Category risk policy · 3 rejected single-source proposals</div>
                </div>
                <div className="why-apollo-dc-item">
                  <div className="why-apollo-dc-item-top">
                    <div className="why-apollo-dc-item-q">"Emergency purchase — copper wire from new supplier, 48-hour requirement?"</div>
                    <div className="why-apollo-dc-confidence why-apollo-conf-building">Escalate — novel</div>
                  </div>
                  <div className="why-apollo-dc-context">Insufficient decision traces for emergency supplier onboarding in this category. Apollo has identified 2 pre-approved emergency suppliers in adjacent categories. Recommending immediate human review — escalating to procurement director.</div>
                  <div className="why-apollo-dc-trace"><span className="why-apollo-trace-dot"></span>No analogous prior decision · Escalating per protocol</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PERSONA TABS ── */}
      <section className="why-apollo-persona-section">
        <div className="why-apollo-persona-inner">
          <div className="why-apollo-s-eyebrow why-apollo-teal-ey" style={{ marginBottom: '16px' }}>
            <span className="why-apollo-s-eyebrow-line why-apollo-teal-ln"></span>What this means for your role
          </div>
          <h2 className="why-apollo-s-title why-apollo-s-title-wh" style={{ marginBottom: '40px' }}>
            The same platform.<br /><em>Two different conversations.</em>
          </h2>

          <div className="why-apollo-persona-tabs">
            <button 
              className={`why-apollo-p-tab ${activePersona === 'cpo' ? 'active-brass' : ''}`}
              onClick={() => setActivePersona('cpo')}
            >
              CPO & CFO View
            </button>
            <button 
              className={`why-apollo-p-tab ${activePersona === 'cio' ? 'active-teal' : ''}`}
              onClick={() => setActivePersona('cio')}
            >
              CIO & IT View
            </button>
          </div>

          {activePersona === 'cpo' && (
            <div className="why-apollo-persona-panel active why-apollo-cxo-grid">
              <div>
                <div className="why-apollo-narrative-block">
                  <div className="why-apollo-narrative-label">The business case</div>
                  <h3 className="why-apollo-narrative-title">Apollo compounds in value the longer it runs — unlike any generic AI tool</h3>
                  <p className="why-apollo-narrative-body">A general AI gives you the same quality of answer on day one as it does on day 500. It has no memory of your organisation, no model of your judgment, and no accumulation of context. <strong>Apollo's value increases with every decision trace it observes, every contextualised data point it processes, and every outcome it validates.</strong> The ROI curve is not flat. It accelerates.</p>
                </div>
                <div className="why-apollo-narrative-block">
                  <div className="why-apollo-narrative-label">For the CFO</div>
                  <h3 className="why-apollo-narrative-title">Three sources of measurable financial return</h3>
                  <p className="why-apollo-narrative-body"><strong>Cost of AI operation falls</strong> as domain-filtered retrieval reduces token consumption by 60–80% per query. <strong>Decision quality improves</strong> as contextually-grounded data replaces ambiguous figures — the right price, the right definition, the right benchmark. <strong>Labour cost reduces</strong> as routine decisions execute autonomously over time, freeing your procurement team for high-value strategic work.</p>
                </div>
                <div className="why-apollo-narrative-block">
                  <div className="why-apollo-narrative-label">For the CPO</div>
                  <h3 className="why-apollo-narrative-title">Your organisation's procurement judgment, institutionalised</h3>
                  <p className="why-apollo-narrative-body">When your best category manager leaves, their judgment leaves with them. Apollo captures that judgment as decision traces — the patterns, the unstated criteria, the risk posture — and makes it available to every buyer in your team. <strong>Institutional knowledge stops being a retention risk and becomes a platform asset.</strong></p>
                </div>
              </div>
              <div className="why-apollo-impact-cards">
                <div className="why-apollo-impact-card">
                  <div className="why-apollo-impact-card-top">
                    <div className="why-apollo-impact-title">Data that means what you think it means</div>
                    <span className="why-apollo-impact-badge why-apollo-badge-brass">CFO Priority</span>
                  </div>
                  <p className="why-apollo-impact-body">When your board asks "what was Q3 procurement spend?", Apollo returns the number that matches your CFO's definition — not Finance's, not Operations', not the ERP's default. Contextual disambiguation means every number is the right number for the person who asked.</p>
                </div>
                <div className="why-apollo-impact-card">
                  <div className="why-apollo-impact-card-top">
                    <div className="why-apollo-impact-title">Negotiation intelligence grounded in real market context</div>
                    <span className="why-apollo-impact-badge why-apollo-badge-brass">CPO Priority</span>
                  </div>
                  <p className="why-apollo-impact-body">Every supplier conversation Apollo supports is backed by correctly-typed market data — the right price benchmark, the right supply signal, the right demand forecast — with source authority and confidence level attached. Your buyers walk into negotiations knowing what the market actually says, not what a general AI happened to retrieve.</p>
                </div>
                <div className="why-apollo-impact-card">
                  <div className="why-apollo-impact-card-top">
                    <div className="why-apollo-impact-title">Autonomous execution of routine decisions</div>
                    <span className="why-apollo-impact-badge why-apollo-badge-teal">Shared Benefit</span>
                  </div>
                  <p className="why-apollo-impact-body">Standard renewals, within-policy reorders, low-risk contract extensions — decisions Apollo has seen enough of to execute with high confidence — no longer require human sign-off. Your team's attention is a scarce resource. Apollo allocates it to decisions that genuinely need it.</p>
                </div>
                <div className="why-apollo-impact-card">
                  <div className="why-apollo-impact-card-top">
                    <div className="why-apollo-impact-title">Procurement intelligence that cannot be replicated by competitors</div>
                    <span className="why-apollo-impact-badge why-apollo-badge-brass">Strategic Moat</span>
                  </div>
                  <p className="why-apollo-impact-body">A generic AI deployed by your competitor works identically to yours on day one. Apollo, trained on your organisation's decision traces, contextual data, and outcomes, becomes progressively more specific to your procurement reality. The longer you run it, the harder it is to replicate.</p>
                </div>
              </div>
            </div>
          )}

          {activePersona === 'cio' && (
            <div className="why-apollo-persona-panel active why-apollo-cio-grid">
              <div>
                <div className="why-apollo-arch-block">
                  <div className="why-apollo-arch-label">The architecture question</div>
                  <h3 className="why-apollo-arch-title">Why bolting memory onto a general AI is not the same as a purpose-built procurement intelligence layer</h3>
                  <p className="why-apollo-arch-body">
                    Enterprise versions of Claude, GPT, and Gemini can be given memory via system prompts and retrieval augmentation. Your team may already be doing this. The result is a general model that is aware of some context — but it is not a domain-intelligent system. <strong>The difference is architectural, not cosmetic.</strong> Apollo is not a wrapper around a general AI. It is a procurement-native intelligence layer that general AI models pass through.
                  </p>
                  <div className="why-apollo-arch-specs">
                    <div className="why-apollo-arch-spec">
                      <span className="why-apollo-spec-icon teal">01</span>
                      <div className="why-apollo-spec-text"><strong>Domain-typed data pipeline</strong> — Every data object entering Apollo carries structured metadata: price type, source authority, epistemic classification, organisational definition context, and user role. This is enforced at ingestion, not inferred at query time.</div>
                    </div>
                    <div className="why-apollo-arch-spec">
                      <span className="why-apollo-spec-icon teal">02</span>
                      <div className="why-apollo-spec-text"><strong>Filter-first retrieval architecture</strong> — Apollo's query engine applies procurement domain logic before any data is retrieved from your ERP or external sources. The LLM receives a filtered, normalised, contextually-complete dataset — not a truncated slice of everything. Token cost drops 60–80%. Answer completeness is guaranteed by construction.</div>
                    </div>
                    <div className="why-apollo-arch-spec">
                      <span className="why-apollo-spec-icon brass">03</span>
                      <div className="why-apollo-spec-text"><strong>Persistent organisational memory</strong> — Apollo maintains a decision knowledge graph: structured records of every procurement decision observed, the context under which it was made, and the outcome. This is not a vector store of documents. It is typed, relational decision intelligence that grows with every interaction.</div>
                    </div>
                    <div className="why-apollo-arch-spec">
                      <span className="why-apollo-spec-icon brass">04</span>
                      <div className="why-apollo-spec-text"><strong>Semantic disambiguation layer</strong> — Before retrieval, Apollo resolves metric definitions to the correct version for the requesting user's role and department.</div>
                    </div>
                    <div className="why-apollo-arch-spec">
                      <span className="why-apollo-spec-icon slate">05</span>
                      <div className="why-apollo-spec-text"><strong>Your environment, always</strong> — Apollo runs inside your infrastructure — on-premise or private cloud. No procurement data transits external servers.</div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="why-apollo-s-eyebrow why-apollo-slate-ey" style={{ marginBottom: '16px' }}>
                  <span className="why-apollo-s-eyebrow-line why-apollo-slate-ln"></span>Architecture contrast
                </div>
                <div className="why-apollo-terminal" style={{ marginBottom: '24px' }}>
                  <div className="why-apollo-term-bar">
                    <div className="why-apollo-t-dot" style={{ background: '#E05050', opacity: .6 }}></div>
                    <div className="why-apollo-t-dot" style={{ background: '#F0A028', opacity: .6 }}></div>
                    <div className="why-apollo-t-dot" style={{ background: '#2DBF7E', opacity: .6 }}></div>
                    <span className="why-apollo-term-lbl">Generic AI — data flow</span>
                  </div>
                  <div className="why-apollo-term-body">
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-sec">// query: "copper spot price for negotiation"</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">retrieval    </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-r">all_price_records</span> <span className="why-apollo-t-cmt">// 12,400 rows</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">context_sent </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-r">truncated</span> <span className="why-apollo-t-cmt">// context limit hit</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">price_type   </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-r">unknown</span> <span className="why-apollo-t-cmt">// not typed at ingestion</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">result       </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-r">"$9,240 — copper price"</span></div>
                  </div>
                </div>
                <div className="why-apollo-terminal">
                  <div className="why-apollo-term-bar">
                    <div className="why-apollo-t-dot" style={{ background: '#E05050', opacity: .6 }}></div>
                    <div className="why-apollo-t-dot" style={{ background: '#F0A028', opacity: .6 }}></div>
                    <div className="why-apollo-t-dot" style={{ background: '#2DBF7E', opacity: .6 }}></div>
                    <span className="why-apollo-term-lbl">Apollo — data flow</span>
                  </div>
                  <div className="why-apollo-term-body">
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-sec">// query: "copper spot price for negotiation"</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">intent_parse </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-s">"negotiation_benchmark"</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">filter       </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-s">price_type=spot, grade=A, today</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">records_sent </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-g">3</span> <span className="why-apollo-t-cmt">// not 12,400</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">provenance   </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-g">LME · CIF Rotterdam · USD · today</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">authority    </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-g">exchange_official</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">result       </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-g">"$9,240 LME spot — valid benchmark"</span></div>
                    <div className="why-apollo-t-ln"><span className="why-apollo-t-k">token_delta  </span> <span className="why-apollo-t-cmt">: </span><span className="why-apollo-t-g">↓ 74% vs generic retrieval</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="why-apollo-sec-light">
        <div className="why-apollo-sec-inner">
          <div className="why-apollo-sec-header">
            <div className="why-apollo-s-eyebrow why-apollo-brass-ey">
              <span className="why-apollo-s-eyebrow-line why-apollo-brass-ln"></span>The Honest Comparison
            </div>
            <h2 className="why-apollo-s-title">Apollo vs. general enterprise AI<br /><em>on what procurement actually needs.</em></h2>
            <p className="why-apollo-s-sub">Claude, ChatGPT, and Gemini are exceptional general-purpose models. This is not a critique of their capability — it is a statement about what procurement intelligence specifically requires, and why a general model alone cannot provide it.</p>
          </div>

          <div className="why-apollo-comp-wrap">
            <table className="why-apollo-comp-table">
              <thead>
                <tr>
                  <th className="why-apollo-th-c">Capability</th>
                  <th className="why-apollo-th-g">General Enterprise AI<br /><span style={{ fontSize: '.58rem', opacity: .7, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Claude / GPT / Gemini + ERP plugin</span></th>
                  <th className="why-apollo-th-a">SCMmax Apollo</th>
                </tr>
              </thead>
              <tbody>
                <tr className="why-apollo-row-group"><td colSpan="3">Data Intelligence</td></tr>
                <tr>
                  <td className="why-apollo-td-c">Price data with provenance context</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Price retrieved without type, grade, Incoterms, or source authority</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Full provenance metadata — price type, grade, location, currency, source authority — typed and passed with every figure</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Epistemic classification of market signals</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> All external text weighted equally — fact, opinion, forecast indistinguishable</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Every signal classified: verified fact / trend / analyst opinion / forecast / commodity-adjacent — before the model reasons on it</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Semantic disambiguation of internal metrics</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Returns whichever definition the ERP defaults to — user role not considered</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Applies correct metric definition per user role, department, and decision context before retrieval</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Filter-first retrieval (not retrieve-then-truncate)</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cp">~</span> Retrieves broadly then drops what exceeds context — silent gaps</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Domain logic applied pre-retrieval — only relevant, complete data passed to the model</td>
                </tr>

                <tr className="why-apollo-row-group"><td colSpan="3">Organisational Intelligence</td></tr>
                <tr>
                  <td className="why-apollo-td-c">Captures organisational decision patterns</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Stateless between sessions — no memory of prior decisions</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Persistent decision knowledge graph — every prior decision observed, structured, and available to future reasoning</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Surfaces unstated decision criteria</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Cannot infer criteria not explicitly stated in the query or system prompt</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Surfaces patterns from decision traces — criteria that are never written in policy but consistently applied</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Confidence-scored recommendations from prior outcomes</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Confidence is model-intrinsic — not grounded in your organisation's history</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Confidence derived from analogous prior decisions and their outcomes in your specific context</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">Reducing human-in-the-loop over time</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cn">✗</span> Same level of human review required on day 500 as day one</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Routine decisions progressively execute autonomously as confidence accumulates from outcomes</td>
                </tr>

                <tr className="why-apollo-row-group"><td colSpan="3">Infrastructure & Security</td></tr>
                <tr>
                  <td className="why-apollo-td-c">Data stays in your environment</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cp">~</span> Enterprise agreements vary — data routing depends on configuration</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> By architecture — Apollo runs inside your infrastructure. No data transits external servers.</td>
                </tr>
                <tr>
                  <td className="why-apollo-td-c">ERP integration without migration</td>
                  <td className="why-apollo-td-g"><span className="why-apollo-cp">~</span> Plugin-dependent — data quality and normalisation varies</td>
                  <td className="why-apollo-td-a"><span className="why-apollo-cy">✓</span> Read-via-API to SAP, Oracle, Coupa, Ariba — no migration, normalisation handled by Apollo before AI reasoning</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="why-apollo-sec-surface">
        <div className="why-apollo-sec-inner">
          <div className="why-apollo-sec-header">
            <div className="why-apollo-s-eyebrow why-apollo-slate-ey">
              <span className="why-apollo-s-eyebrow-line why-apollo-slate-ln"></span>By the Numbers
            </div>
            <h2 className="why-apollo-s-title">What changes when<br /><em>intelligence has context.</em></h2>
          </div>
          <div className="why-apollo-stats-grid">
            <div className="why-apollo-stat-card">
              <div className="why-apollo-stat-val">74<em>%</em></div>
              <div className="why-apollo-stat-lbl">Reduction in AI token cost per procurement query</div>
              <div className="why-apollo-stat-sub">through domain-filtered retrieval</div>
            </div>
            <div className="why-apollo-stat-card">
              <div className="why-apollo-stat-val">3<em>×</em></div>
              <div className="why-apollo-stat-lbl">Dimensions of context attached to every data point</div>
              <div className="why-apollo-stat-sub">provenance, epistemic status, and organisational definition</div>
            </div>
            <div className="why-apollo-stat-card">
              <div className="why-apollo-stat-val">0</div>
              <div className="why-apollo-stat-lbl">Data leaves your environment</div>
              <div className="why-apollo-stat-sub">Apollo runs inside your own infrastructure</div>
            </div>
            <div className="why-apollo-stat-card">
              <div className="why-apollo-stat-val">60<em>–</em>90</div>
              <div className="why-apollo-stat-lbl">Days from contract signing to Apollo live on your spend</div>
              <div className="why-apollo-stat-sub">no rip-and-replace — connects via API</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ANALOGY ── */}
      <section className="why-apollo-sec-dark">
        <div className="why-apollo-sec-inner">
          <div className="why-apollo-analogy-grid">
            <div>
              <div className="why-apollo-s-eyebrow why-apollo-teal-ey">
                <span className="why-apollo-s-eyebrow-line why-apollo-teal-ln"></span>How we explain it
              </div>
              <h2 className="why-apollo-s-title why-apollo-s-title-wh" style={{ marginBottom: '16px' }}>
                The expert who knows<br /><em>what the number means.</em>
              </h2>
              <p className="why-apollo-s-sub why-apollo-s-sub-wh" style={{ marginBottom: 0 }}>
                The difference between Apollo and a general AI on your data is not the quality of the model. It is the quality of what the model receives — and what it learns over time about your organisation.
              </p>
            </div>
            <div className="why-apollo-analogy-card">
              <div className="why-apollo-analogy-mark">"</div>
              <p className="why-apollo-analogy-quote">
                A general AI connected to your data is a brilliant analyst seeing your files for the first time, every single time. They are smart enough to answer — but they do not know that your Q3 spend figure means something different to your CFO than to your category manager. They do not know that you always dual-source specialty chemicals regardless of what the cost model says.<br /><br />
                Apollo is the analyst who has been in your organisation for five years — and gets smarter every week.
              </p>
              <div className="why-apollo-analogy-src">How we explain it to CPOs and CFOs in the first conversation</div>
            </div>
          </div>
        </div>
      </section>

      <div className="why-apollo-wave" style={{ background: 'var(--slate-900)' }}>
        <svg viewBox="0 0 1440 44" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0 44 L1440 44 L1440 22 Q720 -18 0 22 Z" fill="#0F1421" />
        </svg>
      </div>

      {/* ── CTA ── */}
      <section className="why-apollo-cta-strip">
        <div className="why-apollo-cta-inner">
          <div>
            <div className="why-apollo-cta-title">See Apollo reason on<br /><em>your own procurement data.</em></div>
            <div className="why-apollo-cta-sub">30 minutes. Your spend. Our team. No slides. Bring a question your team actually needs answered.</div>
          </div>
          <div className="why-apollo-cta-btns">
            <button className="btn-ghost" onClick={() => window.open('https://campaigns.scmmax.com/industrial/Apollo_Intelligence_Brief.pdf', '_blank')}>Download Intelligence Brief</button>
            <button className="btn-primary" onClick={handleDemoLink}>Request Executive Briefing</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default WhyApollo
